import React, { useEffect, useRef, useState } from "react";
import { useSelector } from 'react-redux';
import { ToastContainer } from "react-toastify";
import {
  getTeamLeads,
  editUser,
  deleteUser,
  getUsers,
  addUser,
} from "../../services/api/api";
import Loader from "../loader/Loader";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toolbar } from "primereact/toolbar";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Dialog } from "primereact/dialog";
import { Sidebar } from "primereact/sidebar";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";

import {
  fetchData,
  showToast,
  formateName,
  trimObjectValues,
  getSerialNo,
} from "../utils/Function/Helper";
import {
  NewButton,
  DeleteButton,
  ActionButton,
  SearchBar,
  NormalButton,
} from "../utils/Atoms/Atoms";

const UserList = () => {
  const user = useSelector((state) => state.user);    
  
  let emptyData = {
    superadmin_id : user?.userDetails?.ID || null,
    teamlead_id: user?.userDetails?.TeamLeadID || null,
    id: null,
    user_name: "",
    user_email: "",
    user_password: "",
    role: "User",
  };

  const [data, setData] = useState(emptyData);
  const [datas, setDatas] = useState([]);
  const [teamLeadOption, setTeamLeadOption] = useState([]);

  const [errors, setErrors] = useState({});
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedDatas, setSelectedDatas] = useState(null);
  const [popup, setPopup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingapi, setLoadingapi] = useState(true);
  const [buffering, setBuffering] = useState(false);
  const dt = useRef(null);

  // loader
  const loader = () => {
    setBuffering(true);
    setTimeout(() => {
      setBuffering(false);
    }, 500); // 0.5 second delay
  };

  // get datas
  useEffect(() => {
    setDatas([]);
    if (emptyData.superadmin_id) {
      fetchData(() => getUsers(emptyData), setDatas);  
      fetchData(() => getTeamLeads(emptyData), setTeamLeadOption);
    } 
    loader();
  }, [user, loadingapi]);

  // open add popup
  const openDialog = () => {
    setData(emptyData);
    setErrors({});
    setPopup(1);
  };

  // close popup
  const hideDialog = () => {
    setData(emptyData);
    setPopup(null);
  };

  // update inputs
  const onInputChange = (e, name) => {
    let _data = { ...data };
    let val = (e.target && e.target.value).trimStart() || "";
    switch (name) {
      case "user_name": {
        val = formateName(val);
        break;
      }

      case "user_email": {
        val = val.toLowerCase();
        break;
      }

      default: {
        val = val;
        break;
      }
    }
    _data[name] = val;
    if (val) setErrors({ ...errors, [name]: "" }); // Clear error on input change
    setData(_data);
  };

  // reseting the page
  const resetPage = () => {
    hideDialog();
    setLoadingapi((prev) => !prev);
  };

  // handle Api Response
  const handleApiResponse = (response) => {
    if (response?.status) {
      showToast("success", response.message);
      resetPage();
    } else {
      showToast("warning", response?.message || response, "top-center");
    }
  };

  // validate all fields
  const validateField = (finalData) => {
    const newErrors = {};

    // name validation
    if (!finalData.user_name) {
      newErrors.user_name = "Name is required";
    } else {
      
      const isNameExist = datas.some(

        
        (item) =>
          item.id !== finalData.id &&
          item.user_name.toLowerCase() === finalData.user_name.toLowerCase()
      );
      if (isNameExist) {
        showToast("warning", `Name Already Exist !`, "top-center");
        newErrors.user_name = "Name already exist";
      }
    }

    // email validation
    if (!finalData.user_email) {
      newErrors.user_email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(finalData.user_email)) {
      newErrors.user_email = "Please enter a valid email";
    } else {
      const isEmailExist = datas.some(
        (item) =>
          item.id !== finalData.id &&
          item.user_email.toLowerCase() === finalData.user_email.toLowerCase()
      );
      if (isEmailExist) {
        showToast("warning", `Email Already Exist !`, "top-center");
        newErrors.user_email = "Email already exist";
      }
    }

    // password validation
    if (!finalData.user_password) {
      newErrors.user_password = "Password is required";
    } else if (finalData.user_password.length < 6) {
      newErrors.user_password = "Password must be at least 6 characters long";
    }
    if (!finalData.teamlead_id) {
      newErrors.teamlead_id = "Team Lead Name is required";
    } 
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Returns true if no errors
  };

  // add and edit Item
  const addItem = async () => {    
    let _data = trimObjectValues(data);

    // validation
    if (validateField(_data)) {
      // Perform API call
      setLoading(true);
      console.log(data);
      
      const response = _data.id ? await editUser(_data) : await addUser(_data);
      handleApiResponse(response);
      setLoading(false);
    }
  };

  // save and cancel button
  const dataDialogFooter = (
    <React.Fragment>
      <NormalButton
        label="Cancel"
        icon="pi-times"
        outlined={true}
        onClick={hideDialog}
      />
      <NormalButton
        label="Save"
        icon="pi-check"
        disabled={loading}
        onClick={addItem}
      />
    </React.Fragment>
  );

  // add and delete button
  const rightToolbarTemplate = () => {
    return (
      <div className="flex flex-wrap gap-2">
        <SearchBar
          position={"left"}
          onInput={(e) => setGlobalFilter(e.target.value)}
        />
        <NewButton onClick={openDialog} />
        <DeleteButton
          onClick={() => setPopup(3)}
          disabled={!selectedDatas || !selectedDatas.length}
        />
      </div>
    );
  };

  // export button
  const leftToolbarTemplate = () => {
    return <h3 className="m-0 hcolor">Users List</h3>;
  };

  // action buttons
  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex">
        <ActionButton
          icon={"pi-pen-to-square"}
          onClick={() => {
            setData({ ...rowData });
            setErrors({});
            setPopup(1);
          }}
          id={"editTooltip"}
          toolText={"Edit"}
        />
        <ActionButton
          icon={"pi-trash"}
          onClick={() => {
            setData({ ...rowData });
            setPopup(2);
          }}
          id={"deleteTooltip"}
          toolText={"Delete"}
        />
      </div>
    );
  };

  // delete one
  const deleteData = async () => {
    setLoading(true);    
    const response = await deleteUser(data);
    if (response?.status) {
      showToast("success", response.message);
      resetPage();
    } else {
      showToast("warning", response?.message || response);
    }
    setLoading(false);
  };

  // delete many
  const deleteSelectedDatas = async () => {
    setLoading(true);
    try {
      for (const item of selectedDatas) {
        const response = await deleteUser(item);
        if (!response?.status) {
          showToast("warning", response?.message || response);
          return;
        }
      }
      showToast("success", `Users Deleted`);
      setSelectedDatas(null);
      resetPage();
    } catch (error) {
      showToast("error", "An error occurred while deleting data.");
    } finally {
      setLoading(false);
    }
  };

  // delete footer buttons
  const deleteDialogFooter = (callTo) => {
    return (
      <React.Fragment>
        <NormalButton
          label="No"
          icon="pi-times"
          outlined={true}
          onClick={hideDialog}
        />
        <NormalButton
          label="Yes"
          icon="pi-check"
          disabled={loading}
          onClick={callTo}
        />
      </React.Fragment>
    );
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  return (
    <>
      <ToastContainer />
      {buffering ? (
        <Loader />
      ) : (
        <div className="card">
          <Toolbar
            className="mb-4"
            start={leftToolbarTemplate}
            end={rightToolbarTemplate}
          ></Toolbar>

          {/* table */}
          <DataTable
            ref={dt}
            value={datas}
            paginator
            rows={10}
            className="datatable"
            rowsPerPageOptions={[5, 10, 15]}
            globalFilter={globalFilter}
            selection={selectedDatas}
            onSelectionChange={(e) => setSelectedDatas(e.value)}
            selectionMode="checkbox"
            emptyMessage={`No Users Available`}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} datas"
          >
            <Column
              selectionMode="multiple"
              exportable={false}
              style={{ width: "30px" }}
            />
            <Column
              header="S.No"
              body={getSerialNo}
              style={{ width: "30px" }}
            />
            <Column field="teamlead_name" sortable header="Team Lead" />
            <Column field="user_name" sortable header="User Name" />
            <Column field="user_email" sortable header="Email" />
            <Column field="user_password" header="Password" />
            <Column
              header="Action"
              body={actionBodyTemplate}
              exportable={false}
              style={{ width: "50px" }}
            />
          </DataTable>
        </div>
      )}

      {/* 1. add and edit data dialog */}
      <Sidebar
        visible={popup === 1}
        position="right"
        header={<h4 className="mt-3 mb-0">User Details</h4>}
        onHide={hideDialog}
        style={{ width: window.innerWidth < 768 ? "20rem" : "25rem" }}
        aria-label="Sidebar Details"
        closeOnEscape={false}
        dismissable={false}
      >
        <Divider />

        <div className="field mb-3">
          <div>
            <label htmlFor="teamlead_id" className="font-bold mb-1">
              Team Lead Name *
            </label>
          </div>
          <Dropdown
            id="teamlead_id"
            value={data?.teamlead_id?.toString()}
            options={teamLeadOption} // Pass the original data
            optionLabel="teamlead_name" // Use "team_name" as the display label
            optionValue="id" // Use "id" as the underlying value
            onChange={(e) => onInputChange(e, "teamlead_id")}
            placeholder="Select a Team"
            required
            disabled={user?.userDetails?.UserRole !== 'Super Admin'}
            style={{ width: "100%" }}
            className={classNames({
              "p-invalid": errors.teamlead_id,
            })}
          />
          <div>
            {errors.teamlead_id && <small className="p-error">{errors.teamlead_id}</small>}
          </div>
        </div>

        <div className="field mb-3">
          <div>
            <label htmlFor="username" className="font-bold mb-1">
              Name *
            </label>
          </div>
          <InputText
            id="user_name"
            value={data.user_name}
            onChange={(e) => onInputChange(e, "user_name")}
            placeholder="Please Enter Name"
            required
            style={{ width: "100%" }}
            className={classNames({
              "p-invalid": errors.user_name,
            })}
          />
          <div>
            {errors.user_name && (
              <small className="p-error">{errors.user_name}</small>
            )}
          </div>
        </div>

        <div className="field mb-3">
          <div>
            <label htmlFor="email" className="font-bold mb-1">
              Email *
            </label>
          </div>
          <InputText
            id="user_email"
            value={data.user_email}
            onChange={(e) => onInputChange(e, "user_email")}
            placeholder="Please Enter Email"
            required
            style={{ width: "100%" }}
            className={classNames({
              "p-invalid": errors.user_email,
            })}
          />
          <div>
            {errors.user_email && <small className="p-error">{errors.user_email}</small>}
          </div>
        </div>

        <div className="field mb-3">
          <div>
            <label htmlFor="password" className="font-bold mb-1">
              Password *
            </label>
          </div>
          <InputText
            id="user_password"
            value={data.user_password}
            onChange={(e) => onInputChange(e, "user_password")}
            placeholder="Please Enter Password"
            required
            style={{ width: "100%" }}
            className={classNames({
              "p-invalid": errors.user_password,
            })}
          />
          <div>
            {errors.user_password && (
              <small className="p-error">{errors.user_password}</small>
            )}
          </div>
        </div>

        <div className="field mt-5">{dataDialogFooter}</div>
      </Sidebar>

      {/* 2. delete single data dialog */}
      <Dialog
        visible={popup === 2}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={deleteDialogFooter(deleteData)}
        onHide={hideDialog}
      >
        <div>
          <i
            className="pi pi-exclamation-triangle me-3"
            style={{ fontSize: "2rem" }}
          />
          {data && (
            <span>
              Are you sure you want to delete <b>{data.teamlead_name}</b> ?
            </span>
          )}
        </div>
      </Dialog>

      {/* 3. delete many datas dialog */}
      <Dialog
        visible={popup === 3}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={deleteDialogFooter(deleteSelectedDatas)}
        onHide={hideDialog}
      >
        <div>
          <i
            className="pi pi-exclamation-triangle me-3"
            style={{ fontSize: "2rem" }}
          />
          {data && (
            <span>Are you sure you want to delete the selected Datas ?</span>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default UserList;
